import { useConnectedEquipment } from "../contexts/ConnectedEquipmentContext";

/**
 * Single source of truth for the live heart-rate number.
 *
 * HR historically came from two disconnected places: a direct Web Bluetooth
 * strap (`equipment.hr`, which feeds telemetry + charts) and an ESP32-relayed
 * poll (`useDeviceHr`, shown only in the coach footer). This hook collapses
 * them to one value — the strap is primary (lowest latency, already recorded),
 * with the polled device value as an automatic fallback.
 *
 * Reactivity comes from the equipment context, which re-renders on the strap's
 * (throttled) snapshot updates; pass the device poll value in as `fallbackBpm`
 * so we don't spin up a second poller.
 *
 * @param {number|null} fallbackBpm - ESP32-relayed bpm from `useDeviceHr`.
 * @returns {{ bpm: number|null, source: "strap"|"device"|null }}
 */
export default function useLiveHr(fallbackBpm = null) {
  const { hr } = useConnectedEquipment();
  const strapBpm = hr?.snapshot?.bpm ?? null;
  if (strapBpm != null) return { bpm: strapBpm, source: "strap" };
  if (fallbackBpm != null) return { bpm: fallbackBpm, source: "device" };
  return { bpm: null, source: null };
}
